import React from 'react';
import { Checkbox, Table, Tag } from 'antd';
import { checkValue, formatDate } from 'utils';
import {
  paymentStatusToText,
  paymentStatuses,
  cardPaymentType,
  cardPaymentTypesList,
} from 'utils/handbooks/paymentsHandbook';
import {
  COLUMN_LARGE,
  PAGE_SIZE_OPTIONS,
  SCROLL_Y_WITH_SUMMARY,
} from 'constants/table';
import { filterColumns, priceRender, renderWithTooltip } from 'utils/table';
import { SumRange } from 'components/Filters/SumRange';
import { SelectTableFilter } from 'components/Filters/SelectTableFilter';
import { DatePickerFilter } from 'components/Filters/DatePickerFilter';
import { InputFilter } from 'components/Filters/InputFilter';
import { FilterOptionsWrapper } from 'ui/Filter';
import { FilterIcon } from 'components/Filters/FilterIcon';
import { RangePickerFilter } from 'components/Filters/RangePickerFilter';
import { thousandFormatter } from 'utils/forms/formatter';

function CardPaymentsTable({
  items,
  loading,
  totalRows,
  current,
  pageSize,
  filters = {},
  summary,
  allowFilter = false,
  onRowClick,
  onChange,
  onFilter,
}) {
  const {
    status,
    ordernum,
    f_created,
    t_created,
    send_date,
    f_sum,
    t_sum,
    visa,
    desc,
    pay_type,
  } = filters;

  const columns = [
    {
      title: 'Клиент',
      key: 'company_name',
      dataIndex: 'company_name',
      // fixed: 'left',
      width: 250,
      render: (value) => renderWithTooltip(value, 60),
    },
    {
      width: COLUMN_LARGE,
      title: 'Статус',
      key: 'status',
      dataIndex: 'status',
      render: (status) => {
        return <Tag>{paymentStatusToText[status] || status}</Tag>;
      },
      filterDropdown: () => {
        return (
          <SelectTableFilter
            multiple
            value={status}
            dataSourse={paymentStatuses}
            placeholder="Выберите cтатус"
            getSelectedItem={(status) => onFilter({ status })}
          />
        );
      },
      filterIcon: () => <FilterIcon isActive={status.length} />,
    },
    {
      title: 'Дата создания',
      key: 'create_date',
      dataIndex: 'create_date',
      render: (date) => {
        if (date === checkValue(date))
          return formatDate(date, 'DD.MM.YYYY, HH:mm');
        return checkValue(date);
      },
      filterDropdown: () => {
        return (
          <RangePickerFilter
            value={[f_created, t_created]}
            onFilterChange={([start, end]) => {
              onFilter({ f_created: start, t_created: end });
            }}
          />
        );
      },
      filterIcon: () => <FilterIcon isActive={f_created && t_created} />,
    },
    {
      title: 'Дата проведения',
      key: 'send_date',
      dataIndex: 'send_date',
      render: (date) => {
        if (date === checkValue(date)) return formatDate(date);
        return checkValue(date);
      },
      filterDropdown: () => {
        return (
          <DatePickerFilter
            value={send_date}
            getDate={(send_date) => onFilter({ send_date })}
          />
        );
      },
      filterIcon: () => <FilterIcon isActive={send_date} />,
    },
    {
      title: 'Сумма',
      key: 'amount',
      dataIndex: 'amount',
      render: priceRender,
      filterDropdown: () => {
        return (
          <SumRange
            fromValue={f_sum}
            toValue={t_sum}
            getRange={(first, second) =>
              onFilter({ f_sum: first, t_sum: second })
            }
          />
        );
      },
      filterIcon: () => <FilterIcon isActive={f_sum || t_sum} />,
    },
    {
      title: 'Скидка (VISA)',
      key: 'visa_amount',
      dataIndex: 'visa_amount',
      render: priceRender,
      filterDropdown: () => {
        return (
          <FilterOptionsWrapper>
            <Checkbox
              checked={visa}
              onChange={(e) => onFilter({ visa: e.target.checked })}
            >
              С VISA
            </Checkbox>
          </FilterOptionsWrapper>
        );
      },
      filterIcon: () => <FilterIcon isActive={visa} />,
    },
    {
      title: 'Комиссия',
      key: 'fee',
      dataIndex: 'fee',
      render: priceRender,
    },
    {
      title: 'Комиссия (Payda)',
      key: 'payda_fee',
      dataIndex: 'payda_fee',
      render: priceRender,
    },
    {
      title: '№',
      key: 'order',
      dataIndex: 'order',
      filterDropdown: () => {
        return (
          <InputFilter
            value={ordernum}
            placeholder="Поиск по номеру заказа"
            onFilterChange={(ordernum) => onFilter({ ordernum })}
          />
        );
      },
      filterIcon: () => <FilterIcon search isActive={ordernum} />,
    },
    {
      title: 'РРН',
      key: 'rrn',
      dataIndex: 'rrn',
      render: checkValue,
    },
    {
      title: 'Тип',
      key: 'type',
      dataIndex: 'type',
      render: (value) => checkValue(cardPaymentType[value]),
      filterDropdown: () => {
        return (
          <SelectTableFilter
            value={pay_type}
            dataSourse={cardPaymentTypesList}
            placeholder="Выберите тип"
            getSelectedItem={(pay_type) => onFilter({ pay_type })}
          />
        );
      },
      filterIcon: () => <FilterIcon isActive={pay_type} />,
    },
    {
      title: 'Описание',
      key: 'desc',
      dataIndex: 'desc',
      render: (value) => renderWithTooltip(value),
      filterDropdown: () => {
        return (
          <InputFilter
            value={desc}
            placeholder="Поиск по описанию"
            onFilterChange={(desc) => onFilter({ desc })}
          />
        );
      },
      filterIcon: () => <FilterIcon search isActive={desc} />,
    },
    {
      title: 'Проведен',
      key: 'send_date',
      dataIndex: 'send_date',
      render: (date) => {
        if (!date) {
          return '---';
        }
        return formatDate(date, 'DD.MM.YYYY, HH.mm');
      },
    },
  ];

  const renderSummary = () => {
    const { total_amount, total_fee, total_payda_fee, total_visa_amount } =
      summary;

    return (
      <Table.Summary fixed>
        <Table.Summary.Row>
          <Table.Summary.Cell>Итого</Table.Summary.Cell>
          <Table.Summary.Cell>&nbsp;</Table.Summary.Cell>
          <Table.Summary.Cell>&nbsp;</Table.Summary.Cell>
          <Table.Summary.Cell>&nbsp;</Table.Summary.Cell>
          <Table.Summary.Cell>
            {thousandFormatter(total_amount)} ₸
          </Table.Summary.Cell>
          <Table.Summary.Cell>
            {thousandFormatter(total_visa_amount)} ₸
          </Table.Summary.Cell>
          <Table.Summary.Cell>
            {thousandFormatter(total_fee)} ₸
          </Table.Summary.Cell>
          <Table.Summary.Cell>
            {thousandFormatter(total_payda_fee)} ₸
          </Table.Summary.Cell>
        </Table.Summary.Row>
      </Table.Summary>
    );
  };

  return (
    <Table
      columns={filterColumns(columns, allowFilter)}
      dataSource={items}
      loading={loading}
      summary={summary ? renderSummary : null}
      scroll={{ x: 2000, y: SCROLL_Y_WITH_SUMMARY }}
      rowClassName={(record) =>
        record?.priority === 1 ? 'table-row-danger' : ''
      }
      pagination={{
        total: totalRows,
        current,
        pageSize,
        showSizeChanger: true,
        pageSizeOptions: PAGE_SIZE_OPTIONS,
      }}
      rowKey="id"
      onRow={(record) => {
        if (onRowClick) {
          return {
            onDoubleClick: () => onRowClick(record),
          };
        }

        return null;
      }}
      onChange={onChange}
    />
  );
}

export { CardPaymentsTable };
